import { graphql } from 'react-relay';

import { markErrorAsHandled } from '@confluence/graphql-error-processor/entry-points/GraphqlErrorProcessor.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { timeGuard } from '@confluence/query-preloader-tools/entry-points/timeGuard.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';

import type {
	preloadOutboundBylineSsrStateQuery$data,
	preloadOutboundBylineSsrStateQuery$variables,
} from './__generated__/preloadOutboundBylineSsrStateQuery.graphql.ts';

const OUTBOUND_BYLINE_PRELOAD_TIMEOUT_MS = 300;
const OUTBOUND_BYLINE_PRELOAD_TIMED_OUT = Symbol('outbound-byline-preload-timed-out');

type OutboundBylineSsrStatus = 'present' | 'empty' | 'unavailable';
type OutboundBylineUnavailableReason = 'malformed-response' | 'query-error' | 'timeout';
type OutboundBylineUnavailableCauseCategory = 'error' | 'graphql' | 'none' | 'non-error';
type PreloadOutgoingLinksResult = Awaited<ReturnType<typeof preloadOutgoingLinks>>;

const OutboundBylineSsrStateQuery = graphql`
	query preloadOutboundBylineSsrStateQuery($contentId: ID!) {
		content(id: $contentId) {
			nodes {
				id
				outgoingLinks {
					externalOutgoingLinks(first: 3) {
						nodes {
							url
						}
					}
					internalOutgoingLinks(first: 3) {
						nodes {
							id
							links {
								base
								webui
							}
						}
					}
				}
			}
		}
	}
`;

const preloadOutgoingLinks = (contentId: string) =>
	query<preloadOutboundBylineSsrStateQuery$data, preloadOutboundBylineSsrStateQuery$variables>({
		query: OutboundBylineSsrStateQuery,
		variables: { contentId },
	});

export type OutboundBylineSsrState = Readonly<{
	contentId: string;
	status: OutboundBylineSsrStatus;
}>;

const createState = (contentId: string, status: OutboundBylineSsrStatus): OutboundBylineSsrState =>
	Object.freeze({ contentId, status });

const isNullOrUndefined = (value: unknown): value is null | undefined => {
	if (value === null) {
		return true;
	}
	return value === undefined;
};

type OutgoingLinksCollections<TExternalNodes, TInternalNodes> = Readonly<{
	externalNodes: TExternalNodes | null | undefined;
	internalNodes: TInternalNodes | null | undefined;
}>;

type CompleteOutgoingLinksCollections<TExternalNodes, TInternalNodes> = Readonly<{
	externalNodes: TExternalNodes;
	internalNodes: TInternalNodes;
}>;

const isValidOutgoingLinksResponse = <TExternalNodes, TInternalNodes>(
	actualContentId: string | null | undefined,
	expectedContentId: string,
	collections: OutgoingLinksCollections<TExternalNodes, TInternalNodes>,
): collections is CompleteOutgoingLinksCollections<TExternalNodes, TInternalNodes> => {
	if (actualContentId !== expectedContentId) {
		return false;
	}
	if (isNullOrUndefined(collections.externalNodes)) {
		return false;
	}
	if (isNullOrUndefined(collections.internalNodes)) {
		return false;
	}
	return true;
};

const getUnavailableCauseCategory = (cause?: unknown): OutboundBylineUnavailableCauseCategory => {
	if (isNullOrUndefined(cause)) {
		return 'none';
	}
	if (!(cause instanceof Error)) {
		return 'non-error';
	}
	if (cause.name === 'GraphQLError') {
		return 'graphql';
	}
	return 'error';
};

const reportUnavailable = (reason: OutboundBylineUnavailableReason, cause?: unknown): void => {
	const causeCategory = getUnavailableCauseCategory(cause);

	ssrPrepareErrorsClient.submitError(
		new Error(`Outbound byline SSR state unavailable: ${reason}`, { cause }),
		{
			operation: 'preload-outbound-byline-ssr-state',
			reason,
			causeCategory,
		},
	);

	if (cause instanceof Error) {
		markErrorAsHandled(cause);
	}
};

const normalizeOutgoingLinks = (
	contentId: string,
	result: PreloadOutgoingLinksResult,
): OutboundBylineSsrState => {
	if (result.hasErrors || result.errors?.length) {
		reportUnavailable('query-error', result.errors?.[0]);
		return createState(contentId, 'unavailable');
	}

	const contentNodes = result.data?.content?.nodes;
	if (isNullOrUndefined(contentNodes) || contentNodes.length !== 1) {
		reportUnavailable('malformed-response');
		return createState(contentId, 'unavailable');
	}

	const content = contentNodes[0];
	const collections = {
		externalNodes: content?.outgoingLinks?.externalOutgoingLinks?.nodes,
		internalNodes: content?.outgoingLinks?.internalOutgoingLinks?.nodes,
	};
	if (!isValidOutgoingLinksResponse(content?.id, contentId, collections)) {
		reportUnavailable('malformed-response');
		return createState(contentId, 'unavailable');
	}
	const { externalNodes, internalNodes } = collections;

	const hasMalformedExternalNode = externalNodes.some(
		(node) => typeof node?.url !== 'string' || node.url.length === 0,
	);
	const hasMalformedInternalNode = internalNodes.some((node) => {
		const base = node?.links?.base;
		const webui = node?.links?.webui;
		return !(
			(typeof base === 'string' && base.length > 0) ||
			(typeof webui === 'string' && webui.length > 0)
		);
	});
	if (hasMalformedExternalNode || hasMalformedInternalNode) {
		reportUnavailable('malformed-response');
		return createState(contentId, 'unavailable');
	}

	return createState(
		contentId,
		externalNodes.length > 0 || internalNodes.length > 0 ? 'present' : 'empty',
	);
};

export const preloadOutboundBylineSsrState = async (
	contentId: string,
): Promise<OutboundBylineSsrState> => {
	try {
		const outgoingLinksPromise = preloadOutgoingLinks(contentId);
		const result = await timeGuard<
			PreloadOutgoingLinksResult | typeof OUTBOUND_BYLINE_PRELOAD_TIMED_OUT
		>(
			() => outgoingLinksPromise,
			OUTBOUND_BYLINE_PRELOAD_TIMED_OUT,
			OUTBOUND_BYLINE_PRELOAD_TIMEOUT_MS,
		);

		if (result === OUTBOUND_BYLINE_PRELOAD_TIMED_OUT) {
			reportUnavailable('timeout');
			return createState(contentId, 'unavailable');
		}

		return normalizeOutgoingLinks(contentId, result);
	} catch (cause) {
		reportUnavailable('query-error', cause);
		return createState(contentId, 'unavailable');
	}
};
